import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CheckIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../axiosConfig';
import { useResendMailStore } from '../../store/resendMailStore';

type Props = {
  id: number;
  clubNumberCountry: string;
};

type State = 'idle' | 'loading' | 'error';

type ResendMailResponse = {
  status?: string;
  transaction_id?: string;
  mail?: {
    club?: string;
    recipients?: string[];
    from?: string | null;
    subject?: string;
    message?: string;
    attachments?: string[];
  };
  error?: string;
  message?: string;
};

/**
 * Sendet die "Melde- und Zahlungsquittung" einer Anmeldung erneut an Verband
 * und Club. Danach ist der Button für 30 Minuten gesperrt (siehe
 * resendMailStore), damit derselbe Club nicht mehrfach angeschrieben wird.
 */
const ResendMailButton: React.FC<Props> = ({ id, clubNumberCountry }) => {
  const [state, setState] = useState<State>('idle');
  const [now, setNow] = useState<number>(() => Date.now());
  const lock = useResendMailStore((store) => store.locks[String(id)]);
  const markSent = useResendMailStore((store) => store.markSent);

  const isLocked = !!lock && lock.until > now;

  // Sperre selbstständig auslaufen lassen, ohne dass neu geladen werden muss.
  useEffect(() => {
    if (!isLocked) return;

    const timer = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, [isLocked]);

  const handleClick = useCallback(async () => {
    if (state === 'loading' || isLocked) return;

    try {
      setState('loading');
      const body = { id, club_number_country: clubNumberCountry } as const;
      const response = await axiosInstance.post<ResendMailResponse>(
        '/association/invoice/resend-mail',
        body,
      );
      const data = response.data;

      console.log('invoice/resend-mail response:', data);

      if (data?.status !== 'success') {
        setState('error');
        return;
      }

      markSent(id);
      setNow(Date.now());
      setState('idle');
    } catch (error) {
      console.error('invoice/resend-mail error:', error);
      setState('error');
    }
  }, [clubNumberCountry, id, isLocked, markSent, state]);

  if (state === 'loading') {
    return (
      <div className="h-6 px-2 flex items-center justify-center">
        <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-500" />
      </div>
    );
  }

  const baseClasses =
    'h-6 px-2 border rounded-sm transition-all duration-300 flex items-center justify-center gap-1 font-bold text-sm';

  let variantClasses: string;
  let label: string;
  let title: string;
  let icon: React.ReactNode;

  if (isLocked && lock) {
    const remainingMinutes = Math.max(1, Math.ceil((lock.until - now) / 60000));

    variantClasses =
      'bg-green-50 border-green-300 text-[#52A445] cursor-not-allowed';
    label = 'Gesendet';
    title = `Bereits versendet. Erneut möglich in ${remainingMinutes} ${remainingMinutes === 1 ? 'Minute' : 'Minuten'}.`;
    icon = <CheckIcon className="w-4 h-4" />;
  } else if (state === 'error') {
    variantClasses = 'bg-red-50 border-red-300 text-red-600';
    label = 'Fehler';
    title = 'Der Versand ist fehlgeschlagen. Details stehen in der Konsole.';
    icon = <ExclamationTriangleIcon className="w-4 h-4" />;
  } else {
    variantClasses =
      'bg-gradient-to-r from-blue-500/100 to-blue-400 border-blue-500/20 text-white shadow-md shadow-blue-500/20 hover:brightness-110';
    label = 'Erneut senden';
    title = 'Quittung erneut per E-Mail an Verband und Club senden';
    icon = <EnvelopeIcon className="w-4 h-4" />;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLocked}
      title={title}
      className={`${baseClasses} ${variantClasses}`}
    >
      {icon}
      <span className="text-xs whitespace-nowrap">{label}</span>
    </button>
  );
};

export default ResendMailButton;
